import { debounce } from 'lodash';

import app from '../../app';
import { events } from './events';
import { convertFloorNumber, convertRoomsCount, convertHouseId } from './converters';
import { ERegions } from '../../../../../shared/constants';

app.factory('priceEstimatorService', [
  'apiService', 'categoryService', 'adStateService', 'adListenerService', '$rootScope', '$q', '$httpParamSerializer',
  (apiService, categoryService, adStateService, adListenerService, $rootScope, $q, $httpParamSerializer) =>
{
  const watchGroup = adListenerService.createGroup('priceEstimatorService');
  $rootScope.$on(events.STOP_WATCHING, () => {
    watchGroup.stopWatchers();
  });

  let houseId;
  let roomsCount;
  let floorNumber;
  let addressArray = [];

  const replaceNullToUndefined = (value) => value !== null ? value : undefined;
  const removeNullFromArr = (arr) => arr.map(replaceNullToUndefined);

  const getPriceEstimatorWatchArray = () => [
    () => houseId,
    (ad) => ad.totalArea,
    () => roomsCount,
    (ad) => ad.windowsViewType,
    (ad) => ad.balconiesCount > 0 || ad.loggiasCount > 0,
    (ad) => ad.childrenAllowed,
    (ad) => ad.hasConditioner,
    (ad) => ad.petsAllowed,
    (ad) => ad.bargainTerms.tenantsType === 'any',
    (ad) => ad.bargainTerms.leaseTermType,
    (ad) => ad.bargainTerms.mortgageAllowed,
    (ad) => ad.bargainTerms.saleType,
    () => floorNumber,
    (ad) => ad.repairType
  ];

  const getRequestData = ([
    houseId,
    totalArea,
    roomsCount,
    windowsViewType,
    hasBalcony,
    childrenAllowed,
    hasConditioner,
    petsAllowed,
    anyoneAllowed,
    leaseTerm,
    mortgageAllowed,
    saleType,
    floorNumber,
    repairType
  ]) => ({
    totalArea,
    houseId,
    roomsCount,
    rentModificators: {
      windowsViewType,
      hasBalcony,
      childrenAllowed,
      hasConditioner,
      petsAllowed,
      anyoneAllowed,
      leaseTerm,
      repairType,
      withRegistration: false,
      withContract: true
    },
    saleModificators: {
      mortgageAllowed,
      windowsViewType,
      saleType,
      floorNumber,
      repairType
    },
  });

  let lastResponse = {};

  const getFlattenObj = obj => {
    let res = {};
    for (const objKey in obj) {
      const objItem = obj[objKey];
      if (typeof objItem === 'object' && objItem !== null) {
        res = {
          ...res,
          ...getFlattenObj(objItem)
        };
      }
      else {
        res[objKey] = objItem;
      }
    }
    return res;
  };

  const normalizeValue = value => {
    if (typeof value === 'boolean') {
      return value === true ? 1 : 0
    }
    return value;
  }

  const normalizeUrlData = urlData => {
    const normalizedData = {};
    for (const itemKey in urlData) {
      const itemValue = urlData[itemKey];

      if (itemKey === 'windowsViewType' && angular.isDefined(itemValue)) {
        const itemValueLowerCase = itemValue.toLowerCase();
        normalizedData['hasYardView'] = itemValueLowerCase.indexOf('yard') === -1 ? 0 : 1;
        normalizedData['hasStreetView'] = itemValueLowerCase.indexOf('street') === -1 ? 0 : 1;
      } else {
        normalizedData[itemKey] = normalizeValue(itemValue);
      }
    }
    return normalizedData;
  };

  const computeMoreLink = requestData => {
    const urlData = normalizeUrlData(getFlattenObj(requestData));
    return '/kalkulator-nedvizhimosti/?' + $httpParamSerializer(urlData);
  };

  const computeEstimatedPrice = (ad = {}, estimation = {}, currency = {}) => {
    const { totalArea: area, category } = ad;
    const { dealType } = categoryService.getInfoByCategory(category);
    const price = (dealType === 'sale') ? estimation.priceSqm : estimation.rentPriceSqm;
    return {
      dealType,
      value: (area * price) / currency.value,
      currency: currency.name
    }
  };

  const updateEstimatedPrice = price => {
    $rootScope.$broadcast(events.ESTIMATED_PRICE_UPDATED, price);
  };

  let currentCurrency = {};
  const setCurrentCurrency = (currency, currencyRates = []) => {
    const currencyItem = currencyRates.find(item => item.currency === currency);
    const currencyRate = angular.isObject(currencyItem) ? currencyItem.rate : undefined;
    const newCurrentCurrency = {
      value: currencyRate,
      name: currency
    };

    if (newCurrentCurrency.value !== currentCurrency.value) {
      currentCurrency = newCurrentCurrency;
    }
  };

  const allowedLocations = [
    ERegions.moscow,
    ERegions.moscowRegion,
    ERegions.spb,
    ERegions.spbRegion,
    ERegions.nizhniyNovgorod,
    ERegions.kazan,
    ERegions.samara,
    ERegions.ufa,
    ERegions.rostovNaDony,
    ERegions.krasnodar
  ];
  const isAllowedLocation = addressArray => addressArray.some(item => allowedLocations.includes(item.id));

  const allowedObjectTypes = ['flat', 'flatShare'];
  const isAllowedCategory = category => {
    if (!category) {
      return false;
    }
    const { objectType, dealType, rentDuration } = categoryService.getInfoByCategory(category);
    if (dealType === 'rent' && rentDuration === 'short') return false;
    return allowedObjectTypes.includes(objectType);
  };

  adListenerService.$watchGroup([
    (ad) => ad.category,
    (ad) => ad.geo.address
  ], ([category, address]) => {
    if (angular.isUndefined(category) || !Array.isArray(address)) {
      return;
    }
    if (isAllowedCategory(category) && isAllowedLocation(address)) {
      addressArray = address;
      if (!watchGroup.hasWatchers()) {
        installWatchers();
      }
    } else if (watchGroup.hasWatchers()) {
      $rootScope.$emit(events.STOP_WATCHING);
    }
  });

  let requestPromiseTimeout;
  let requestPromiseId = 0;
  const installWatchers = () => {

    watchGroup.$watch((ad) => ad.geo.address, address => {
      houseId = convertHouseId(address);
    });

    watchGroup.$watch((ad) => ad.roomsCount, adRoomsCount => {
      roomsCount = convertRoomsCount(adRoomsCount);
    });

    watchGroup.$watchGroup([(ad) => ad.floorNumber, (ad) => ad.building.floorsCount], ([adFloorNumber, adFloorsCount]) => {
      floorNumber = convertFloorNumber(adFloorNumber, adFloorsCount);
    });

    watchGroup.$watch((ad) => ad.bargainTerms.currency, currency => {
      const ad = adListenerService.getAd();
      if (angular.isUndefined(currency) || angular.isUndefined(lastResponse.currencyRates)) return;
      setCurrentCurrency(currency, lastResponse.currencyRates);
      const price = computeEstimatedPrice(ad, lastResponse.estimation, currentCurrency);
      updateEstimatedPrice(price);
    });

    const watchNotDebounced = watchArray => {
      const requestData = getRequestData(removeNullFromArr(watchArray));
      const currentRequestPromiseId = ++requestPromiseId;

      if (angular.isDefined(requestData.houseId) && requestData.totalArea && requestData.roomsCount) {
        $rootScope.$broadcast(events.UPDATING_ESTIMATED_PRICE);
        if (requestPromiseTimeout) {
          requestPromiseTimeout.resolve();
        }

        requestPromiseTimeout = $q.defer();

        apiService.getPriceEstimate(requestData, {timeout: requestPromiseTimeout.promise, allowServerErrors: true})
          .then(({estimation, currencyRates}) => {
            if (angular.isObject(estimation)) {
              lastResponse = {
                estimation,
                currencyRates
              };

              const ad = adListenerService.getAd();
              setCurrentCurrency(ad.bargainTerms.currency, lastResponse.currencyRates);

              const price = computeEstimatedPrice(ad, lastResponse.estimation, currentCurrency);
              const moreLink = computeMoreLink({...requestData, address: ad.geo.userInput});
              updateEstimatedPrice({
                ...price,
                moreLink
              });
            }
          })
          .catch(e => {
            lastResponse = {};
            updateEstimatedPrice(computeEstimatedPrice()); // {value: NaN, currency: undefined}
          })
          .finally(() => {
            if (currentRequestPromiseId === requestPromiseId) { // not abort
              requestPromiseTimeout = undefined;
            }
          });
      } else {
        lastResponse = {};
        updateEstimatedPrice(computeEstimatedPrice()); // {value: NaN, currency: undefined}
      }
    };

    const watchDebounced = debounce(watchNotDebounced, 500);
    watchGroup.$watchGroup(getPriceEstimatorWatchArray(), watchDebounced);
  };

  return {
    events
  };
}]);
